import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import logo from "@/assets/logo.png";
import {
  LANDING_NAV_LINKS,
  scrollToSection,
  scrollToTop,
} from "./navConfig";
import { LandingMobileMenu } from "./LandingMobileMenu";
import { BetaBadge } from "@/components/shared/BetaBadge";
import { useAuth } from "@/contexts/AuthContext";

interface LandingHeaderProps {
  menuOpen: boolean;
  onToggleMenu: () => void;
  onCloseMenu: () => void;
}

/**
 * Sticky landing-page header. Renders the logo (scrolls to top), the
 * desktop nav driven by `LANDING_NAV_LINKS`, and the auth actions.
 *
 * Keep this component small and self-contained: editing copy on the
 * Landing page should never require touching this file.
 */
export function LandingHeader({
  menuOpen,
  onToggleMenu,
  onCloseMenu,
}: LandingHeaderProps) {
  const linkClass =
    "text-sm font-medium text-muted-foreground px-3 py-1.5 rounded-md cursor-pointer hover:bg-secondary hover:text-primary transition-colors duration-200";
  const { user, signOut } = useAuth();

  return (
    <header className="border-b border-border bg-background/80 backdrop-blur-sm sticky top-0 z-50 pt-2 lg:pt-0">
      <div className="container mx-auto flex h-12 lg:h-16 items-center justify-between px-4 lg:px-6 pb-2 lg:pb-0">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden h-8 w-8"
            onClick={onToggleMenu}
            aria-label="Toggle menu"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
          <button
            type="button"
            onClick={scrollToTop}
            className="flex items-center gap-2 cursor-pointer"
            aria-label="Scroll to top"
          >
            <img src={logo} alt="A Software Tester" className="h-5 lg:h-8" />
            <BetaBadge className="self-start mt-0.5" />
          </button>
        </div>

        <nav className="hidden lg:flex items-center gap-2" aria-label="Primary">
          {LANDING_NAV_LINKS.map((link) => (
            <button
              key={link.label}
              onClick={() => scrollToSection(link.id)}
              className={linkClass}
            >
              {link.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2 lg:gap-4">
          {user ? (
            <>
              <button
                type="button"
                onClick={() => { void signOut(); }}
                className="hidden lg:inline text-sm font-medium text-muted-foreground px-3 py-1.5 rounded-md hover:bg-secondary hover:text-primary transition-colors duration-200"
              >
                Sign Out
              </button>
              <Button asChild size="sm" className="lg:size-default">
                <Link to="/dashboard">Open Application</Link>
              </Button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="hidden lg:inline text-sm font-medium text-muted-foreground px-3 py-1.5 rounded-md hover:bg-secondary hover:text-primary transition-colors duration-200"
              >
                Sign In
              </Link>
              <Button asChild size="sm" className="lg:size-default">
                <Link to="/signup">Start Preparing</Link>
              </Button>
            </>
          )}
        </div>
      </div>
      {/* Mobile menu lives INSIDE the sticky <header> so it scrolls with
          the burger icon. Rendered as a sibling of the header bar (not
          inside it) it would scroll out of the viewport with page
          content and become invisible after a section jump. */}
      <LandingMobileMenu
        open={menuOpen}
        onClose={onCloseMenu}
      />
    </header>
  );
}